import { useState, useEffect } from "react";
import Select from "components/ui/Select";
import { useAllUnits } from "http-hooks/unit/useUnit";
import { useAllSizes } from "http-hooks/size/useSize";
import { useAllShapes } from "http-hooks/shape/useShape";
import { useAllColors } from "http-hooks/color/useColor";
import { useAllSzGrpDtls, useCreateSzGrpDtl } from "http-hooks/szgrpdtl";
import Input from "components/ui/Input";
import { Switch } from "@headlessui/react";
import {
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingRow,
  EmptyRow,
} from "components/ui/Table";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// Constants for size type options
const sizeTypeOptions = [
  { value: "S", label: "Small" },
  { value: "B", label: "Big" },
];

export default function ManageSizeGroupDetails({ sizeGroup, onClose }) {
  const [selectedUoms, setSelectedUoms] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState(new Set());
  const [selectedShapes, setSelectedShapes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [sizeMapping, setSizeMapping] = useState({}); // Key: "size-unit-sizetype", Value: { pcol, sztype }
  
  // Mutation hook for creating size group details
  const { mutateAsync: createDetail, isPending: isSaving } = useCreateSzGrpDtl();

  // Fetch existing size group details
  const { data: { data: existingDetails } = [], isLoading: isLoadingDetails } = useAllSzGrpDtls({
    szgrpid: sizeGroup.szgrpid
  });

  // Fetch units from unit master with unitidunder = 1 filter (only for SZ type)
  const { data: { data: allUnits } = [], isLoading: isUnitsLoading } = useAllUnits({
    unitidunder: 1,
  }, sizeGroup.type === "SZ");

  // Fetch shapes (only for SHP type)
  const { data: { data: allShapes } = [], isLoading: isShapesLoading } = useAllShapes({}, sizeGroup.type === "SHP");

  // Fetch colors (only for CLR type)
  const { data: { data: allColors } = [], isLoading: isColorsLoading } = useAllColors({}, sizeGroup.type === "CLR");

  // Convert Units to options for multiselect
  const uomOptions = allUnits?.map(unit => ({
    value: unit.unitname,
    label: unit.unitname
  })) || [];

  // Convert Shapes to options for multiselect
  const shapeOptions = allShapes?.map(shape => ({
    value: shape.shapetype,
    label: shape.shapetype
  })) || [];

  // Convert Colors to options for multiselect
  const colorOptions = allColors?.map(color => ({
    value: color.clrname,
    label: color.clrname
  })) || [];

  // Create filters for sizes API call based on selections
  const sizesFilter = {};
  if (selectedUoms.length > 0) {
    sizesFilter.umid = selectedUoms.join(','); // Pass as comma-separated string
  }

  // Fetch sizes based on selected filters (only for SZ type)
  const { 
    data: { data: allSizes } = [], 
    isLoading: isSizesLoading 
  } = useAllSizes(
    sizesFilter,
    sizeGroup.type === "SZ" && selectedUoms.length > 0 // Only enable when UOMs are selected and type is SZ
  );

  // Helper function to create mapping key
  const createMappingKey = (size, unit, sizeType) => `${size.itmsz || size}-${size.umid || unit}-${sizeType || 'S'}`;

  // Load existing data when component mounts or existing details change
  useEffect(() => {
    if (existingDetails && existingDetails.length > 0) {
      console.log("Loading existing details:", existingDetails);
      
      if (sizeGroup.type === "SZ") {
        // For size groups, populate size mapping and selected items
        const newSizeMapping = {};
        const newSelectedSizes = new Set();
        const existingUoms = new Set();
        
        existingDetails.forEach(detail => {
          const mappingKey = createMappingKey(detail.itm_sz, detail.szunit, detail.sztype);
          newSizeMapping[mappingKey] = {
            pcol: detail.pcol?.toString() || '',
            sztype: detail.sztype || 'S'
          };
          
          // We need to find the size ID - this will be handled when sizes are loaded
          existingUoms.add(detail.szunit);
        });
        
        setSizeMapping(newSizeMapping);
        setSelectedUoms(Array.from(existingUoms));
        
      } else if (sizeGroup.type === "SHP") {
        // For shape groups, populate selected shapes
        const shapes = existingDetails.map(detail => detail.itm_sz);
        setSelectedShapes(shapes);
        
      } else if (sizeGroup.type === "CLR") {
        // For color groups, populate selected colors
        const colors = existingDetails.map(detail => detail.itm_sz);
        setSelectedColors(colors);
      }
    }
  }, [existingDetails, sizeGroup.type]);

  // Update selected sizes when sizes are loaded and we have existing mapping
  useEffect(() => {
    if (allSizes && allSizes.length > 0 && Object.keys(sizeMapping).length > 0) {
      const newSelectedSizes = new Set();
      
      allSizes.forEach(size => {
        // Check if this size has any mappings (S or B)
        const mappingKeyS = createMappingKey(size, size.umid, 'S');
        const mappingKeyB = createMappingKey(size, size.umid, 'B');
        if (sizeMapping[mappingKeyS] || sizeMapping[mappingKeyB]) {
          newSelectedSizes.add(size.itmszid);
        }
      });
      
      setSelectedSizes(newSelectedSizes);
    }
  }, [allSizes, sizeMapping]);

  const handleUomChange = (selectedOptions) => {
    const selectedUomValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedUoms(selectedUomValues);
    console.log("Units selected:", selectedUomValues);
  };

  const handleShapeChange = (selectedOptions) => {
    const selectedShapeValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedShapes(selectedShapeValues);
    console.log("Shapes selected:", selectedShapeValues);
  };

  const handleColorChange = (selectedOptions) => {
    const selectedColorValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedColors(selectedColorValues);
    console.log("Colors selected:", selectedColorValues);
  };

  const handleSizeSelection = (size, isChecked) => {
    const newSelectedSizes = new Set(selectedSizes);
    
    if (isChecked) {
      newSelectedSizes.add(size.itmszid);
    } else {
      newSelectedSizes.delete(size.itmszid);
      // Remove all mappings for this size when unchecking
      setSizeMapping(prev => {
        const newMapping = { ...prev };
        ['S', 'B'].forEach(sizeType => {
          const mappingKey = createMappingKey(size, size.umid, sizeType);
          delete newMapping[mappingKey];
        });
        return newMapping;
      });
    }
    setSelectedSizes(newSelectedSizes);
  };

  const getSelectedSizeTypes = (size) => {
    const selectedTypes = [];
    ['S', 'B'].forEach(sizeType => {
      const mappingKey = createMappingKey(size, size.umid, sizeType);
      if (sizeMapping[mappingKey]) {
        selectedTypes.push(sizeTypeOptions.find(option => option.value === sizeType));
      }
    });
    return selectedTypes.filter(Boolean);
  };

  const handleSizeTypeChange = (size, selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
    
    // Remove all existing mappings for this size
    setSizeMapping(prev => {
      const newMapping = { ...prev };
      ['S', 'B'].forEach(sizeType => {
        const mappingKey = createMappingKey(size, size.umid, sizeType);
        delete newMapping[mappingKey];
      });
      
      // Add mappings for selected size types
      selectedValues.forEach(sizeType => {
        const mappingKey = createMappingKey(size, size.umid, sizeType);
        newMapping[mappingKey] = {
          pcol: prev[mappingKey]?.pcol || '',
          sztype: sizeType
        };
      });
      
      return newMapping;
    });
  };

  const getColumnValue = (size, sizeType) => {
    const mappingKey = createMappingKey(size, size.umid, sizeType);
    return sizeMapping[mappingKey]?.pcol || '';
  };



  const handleColumnValueChange = (size, sizeType, value) => {
    const mappingKey = createMappingKey(size, size.umid, sizeType);
    
    setSizeMapping(prev => ({
      ...prev,
      [mappingKey]: {
        ...prev[mappingKey],
        pcol: value
      }
    }));
  };

  const getSelectedCount = () => {
    if (sizeGroup.type === "SZ") return selectedSizes.size;
    if (sizeGroup.type === "SHP") return selectedShapes.length;
    if (sizeGroup.type === "CLR") return selectedColors.length;
    return 0;
  };

  const handleSave = async () => {
    try {
      const detailsToCreate = [];

      if (sizeGroup.type === "SZ") {
        // For sizes - create records from size mapping
        Object.entries(sizeMapping).forEach(([mappingKey, mapping]) => {
          const [itm_sz, szunit, sztype] = mappingKey.split('-');
          
          // Only create if it has a valid column number
          if (mapping.pcol && parseInt(mapping.pcol) > 0) {
            detailsToCreate.push({
              szgrpid: sizeGroup.szgrpid,
              itm_sz: itm_sz,
              szunit: szunit,
              sztype: sztype,
              pcol: parseInt(mapping.pcol)
            });
          }
        });
      } else if (sizeGroup.type === "SHP") {
        // For shapes - create a record for each selected shape
        selectedShapes.forEach((shape, index) => {
          detailsToCreate.push({
            szgrpid: sizeGroup.szgrpid,
            itm_sz: shape,
            szunit: '',
            sztype: '',
            pcol: index + 1 // Auto-number the columns
          });
        });
      } else if (sizeGroup.type === "CLR") {
        // For colors - create a record for each selected color
        selectedColors.forEach((color, index) => {
          detailsToCreate.push({
            szgrpid: sizeGroup.szgrpid,
            itm_sz: color,
            szunit: '',
            sztype: '',
            pcol: index + 1 // Auto-number the columns
          });
        });
      }

      if (detailsToCreate.length === 0) {
        return; // Nothing to save
      }

      // Create all details
      for (const detail of detailsToCreate) {
        await createDetail(detail);
      }

      // Success - close modal
      onClose();
    } catch (error) {
      console.error("Failed to save size group details:", error);
      // Error toast already shown by service
    }
  };

  // Show loading state while fetching existing details
  if (isLoadingDetails) {
    return (
      <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form>
      <div>
        <div className="border-b border-gray-900/10 pb-10">
          <h2 className="text-base font-semibold leading-7 text-gray-900">
            Create Ord Print Group Details
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Name: <span className="font-medium">{sizeGroup.szgrpname}</span>
          </p>
          
          <div className="mt-8 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            {/* UOM Selection - Only for SZ type */}
            {sizeGroup.type === "SZ" && (
              <div className="col-span-full">
                <Select
                  options={uomOptions}
                  value={uomOptions.filter(option => selectedUoms.includes(option.value))}
                  onChange={handleUomChange}
                  label="Unit of Measure (UOM)"
                  placeholder={isUnitsLoading ? "Loading Units..." : "Select Units"}
                  isMulti={true}
                  isClearable={true}
                  isSearchable={true}
                  isLoading={isUnitsLoading}
                />
              </div>
            )}

            {/* Shapes Selection - Only for SHP type */}
            {sizeGroup.type === "SHP" && (
              <div className="col-span-full">
                <Select
                  options={shapeOptions}
                  value={shapeOptions.filter(option => selectedShapes.includes(option.value))}
                  onChange={handleShapeChange}
                  label="Shapes"
                  placeholder={isShapesLoading ? "Loading Shapes..." : "Select Shapes"}
                  isMulti={true}
                  isClearable={true}
                  isSearchable={true}
                  isLoading={isShapesLoading}
                />
              </div>
            )}

            {/* Colors Selection - Only for CLR type */}
            {sizeGroup.type === "CLR" && (
              <div className="col-span-full">
                <Select
                  options={colorOptions}
                  value={colorOptions.filter(option => selectedColors.includes(option.value))}
                  onChange={handleColorChange}
                  label="Colors"
                  placeholder={isColorsLoading ? "Loading Colors..." : "Select Colors"}
                  isMulti={true}
                  isClearable={true}
                  isSearchable={true}
                  isLoading={isColorsLoading}
                />
              </div>
            )}

            {/* Sizes Table - Only for SZ type */}
            {sizeGroup.type === "SZ" && (
              <div className="col-span-full">
                <label className="block text-sm font-medium leading-6 text-gray-900 mb-3">
                  Available Sizes
                </label>
                {selectedUoms.length === 0 ? (
                  <p className="text-sm text-gray-500">Please select at least one UOM to view available sizes</p>
                ) : (
                  <div className="mt-4 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                      <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        {/* Fixed height container with overflow */}
                        <div className="max-h-96 overflow-auto border border-gray-300 rounded-lg">
                          <Table>
                            <THead>
                              <TR>
                                <TH first>Select</TH>
                                <TH>Size</TH>
                                <TH>UOM</TH>
                                <TH>Size Type</TH>
                                <TH last>Column</TH>
                              </TR>
                            </THead>
                            <TBody>
                              {isSizesLoading ? (
                                <LoadingRow colSpan={5} message="Loading sizes..." />
                              ) : allSizes?.length === 0 ? (
                                <EmptyRow colSpan={5} message="No sizes found for selected UOMs" />
                              ) : (
                                allSizes?.map((size) => {
                                  // Get selected size types for this size
                                  const selectedSizeTypes = getSelectedSizeTypes(size);
                                  
                                  return (
                                    <TR key={size.itmszid}>
                                      <TD first>
                                        <Input
                                          type="checkbox"
                                          showLabel={false}
                                          value={selectedSizes.has(size.itmszid)}
                                          onChange={(e) => handleSizeSelection(size, e.target.checked)}
                                        />
                                      </TD>
                                      <TD className="font-medium">
                                        {size.itmsz}
                                      </TD>
                                      <TD>{size.umid}</TD>
                                      <TD>
                                        <Select
                                          options={sizeTypeOptions}
                                          value={selectedSizeTypes}
                                          onChange={(selectedOptions) => handleSizeTypeChange(size, selectedOptions)}
                                          placeholder="Select Types"
                                          isMulti={true}
                                          isClearable={true}
                                          isSearchable={false}
                                          isDisabled={!selectedSizes.has(size.itmszid)}
                                          className="text-sm"
                                        />
                                      </TD>
                                      <TD last>
                                        <div className="flex space-x-2">
                                          {selectedSizeTypes.map((sizeTypeOption, index) => (
                                            <div key={sizeTypeOption.value} className="flex flex-col items-center">
                                              <span className="text-xs text-gray-500 mb-1">
                                                {sizeTypeOption.value}
                                              </span>
                                              <Input
                                                type="number"
                                                showLabel={false}
                                                placeholder="Col"
                                                value={getColumnValue(size, sizeTypeOption.value)}
                                                onChange={(e) => handleColumnValueChange(size, sizeTypeOption.value, e.target.value)}
                                                isDisabled={!selectedSizes.has(size.itmszid)}
                                                className="w-16"
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </TD>
                                    </TR>
                                  );
                                })
                              )}
                            </TBody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
          onClick={onClose}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          type="button"
          className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={getSelectedCount() === 0 || isSaving}
          onClick={handleSave}
        >
          {isSaving ? 'Saving...' : `Save (${getSelectedCount()})`}
        </button>
      </div>
    </form>
  );
} 